import { useState, useRef, useCallback, useEffect } from 'react';
import { useSocket } from '../context/SocketContext';

// FIX: STUN-only ICE config fails to connect when either peer is behind
// symmetric NAT / CGNAT (very common on Indian mobile carriers — Jio,
// Airtel, Vi). A TURN server relays media when a direct peer-to-peer path
// can't be established.
const ICE_CONFIG = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
    { urls: 'stun:stun4.l.google.com:19302' },
    {
      urls: 'turn:openrelay.metered.ca:80',
      username: 'openrelayproject',
      credential: 'openrelayproject',
    },
    {
      urls: 'turn:openrelay.metered.ca:443',
      username: 'openrelayproject',
      credential: 'openrelayproject',
    },
    {
      urls: 'turn:openrelay.metered.ca:443?transport=tcp',
      username: 'openrelayproject',
      credential: 'openrelayproject',
    },
  ],
  iceCandidatePoolSize: 10,
};

const RING_TIMEOUT = 30000;

// ── Safely attach stream to a <video>/<audio> element ────────────────────────
function safeAttach(el, stream, muted = false) {
  if (!el) return;
  try {
    el.srcObject   = stream;
    el.muted       = muted;
    el.volume      = muted ? 0 : 1;
    el.autoplay    = true;
    el.playsInline = true;
    const tryPlay = () => {
      const p = el.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => {
          const retry = () => { el.play().catch(() => {}); document.removeEventListener('click', retry); };
          document.addEventListener('click', retry, { once: true });
        });
      }
    };
    tryPlay();
  } catch {}
}

// Persistent Audio node — guarantees remote audio plays even if ref element is hidden/tiny
const _remoteAudio = { node: null };
function ensureAudioPlays(stream) {
  if (!stream) return;
  try {
    if (!_remoteAudio.node) {
      _remoteAudio.node = new Audio();
      _remoteAudio.node.autoplay = true;
    }
    const a = _remoteAudio.node;
    a.srcObject = stream;
    a.muted     = false;
    a.volume    = 1;
    const tryPlay = () => {
      const p = a.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => {
          const retry = () => { a.play().catch(() => {}); document.removeEventListener('click', retry); };
          document.addEventListener('click', retry, { once: true });
        });
      }
    };
    tryPlay();
  } catch {}
}

/**
 * useCallRTC — SINGLE GLOBAL CALL ENGINE.
 *
 * Meant to be instantiated EXACTLY ONCE for the whole app (inside
 * GlobalCallOverlay, mounted at App root level) and consumed everywhere
 * else via CallContext. Previously every chat page created its OWN separate
 * useCallRTC instance AND GlobalCallOverlay had a second, fully duplicated
 * hand-rolled WebRTC implementation (no TURN server, one-directional
 * addTrack, no transceivers). Two independent peer connections existed
 * depending on whether a chat page happened to be open — causing calls to
 * silently fail (no audio/video either direction) whenever a call was
 * initiated/received outside an open chat page.
 *
 * Now there is ONE engine, ONE peer connection lifecycle, used identically
 * whether or not a chat page is open — matching WhatsApp-style behaviour.
 */
export function useCallRTC() {
  const { socket } = useSocket();

  const [callState,     setCallState]     = useState('idle');   // idle | calling | ringing | active
  const [callType,      setCallType]      = useState('audio');
  const [incomingOffer, setIncomingOffer] = useState(null);
  // displayName = the name to SHOW in the call UI — always the OTHER
  // party's name, regardless of who initiated the call:
  //   - outgoing call: the name of the person/store being called (passed
  //     in by the caller's chat page, e.g. doctor's name, patient's name)
  //   - incoming call: the caller's name, as sent by them in call-offer
  // Previously this stored OUR OWN name on outgoing calls (startCall's
  // 2nd arg was used both as "my name to send" AND "name to display"),
  // so the caller literally saw their own name on the calling screen
  // instead of who they were calling.
  const [displayName,   setDisplayName]   = useState('');
  const [isMuted,       setIsMuted]       = useState(false);
  const [isCamOff,      setIsCamOff]      = useState(false);

  const localVideoRef   = useRef(null);
  const remoteVideoRef  = useRef(null);
  const peerRef         = useRef(null);
  const localStreamRef  = useRef(null);
  const remoteStreamRef = useRef(null);
  const iceBuf          = useRef([]);
  const remoteDescSet   = useRef(false);
  const callStateRef    = useRef('idle');
  const activePeerRef   = useRef(null);   // the id of the OTHER party for the current call
  const ringTimerRef    = useRef(null);
  const ringTimeoutRef  = useRef(null);

  const startRingtone = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const beep = () => {
        if (callStateRef.current === 'idle') return;
        const o = ctx.createOscillator(), g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.frequency.value = 440;
        g.gain.setValueAtTime(0.3, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
        o.start(); o.stop(ctx.currentTime + 0.4);
      };
      beep();
      ringTimerRef.current = setInterval(beep, 1200);
    } catch {}
  }, []);

  const stopRingtone = useCallback(() => {
    clearInterval(ringTimerRef.current);
    clearTimeout(ringTimeoutRef.current);
    ringTimerRef.current  = null;
    ringTimeoutRef.current = null;
  }, []);

  const attachRemote = useCallback((stream) => {
    remoteStreamRef.current = stream;
    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = null;
      safeAttach(remoteVideoRef.current, stream, false);
    }
    ensureAudioPlays(stream);
  }, []);

  const attachPendingRemoteStream = useCallback(() => {
    if (remoteStreamRef.current && remoteVideoRef.current) {
      safeAttach(remoteVideoRef.current, remoteStreamRef.current, false);
    }
    if (localStreamRef.current && localVideoRef.current) {
      safeAttach(localVideoRef.current, localStreamRef.current, true);
    }
  }, []);

  const getMedia = useCallback(async (type) => {
    if (!navigator.mediaDevices?.getUserMedia)
      throw new Error('Microphone not supported — use HTTPS');
    try {
      return await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: type === 'video'
          ? { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' }
          : false,
      });
    } catch (err) {
      if (err.name === 'NotAllowedError') throw new Error('Mic/camera access denied');
      if (err.name === 'NotFoundError')   throw new Error('No microphone found');
      throw new Error('Cannot access media: ' + err.message);
    }
  }, []);

  const flushIce = useCallback(async () => {
    if (!peerRef.current || !remoteDescSet.current || !iceBuf.current.length) return;
    const buf = [...iceBuf.current];
    iceBuf.current = [];
    for (const c of buf) {
      try { await peerRef.current.addIceCandidate(new RTCIceCandidate(c)); } catch {}
    }
  }, []);

  const cleanup = useCallback(() => {
    stopRingtone();
    peerRef.current?.close();
    peerRef.current      = null;
    remoteDescSet.current = false;
    iceBuf.current       = [];
    localStreamRef.current?.getTracks().forEach(t => t.stop());
    localStreamRef.current  = null;
    remoteStreamRef.current = null;
    activePeerRef.current   = null;
    if (localVideoRef.current)  { localVideoRef.current.srcObject  = null; }
    if (remoteVideoRef.current) { remoteVideoRef.current.srcObject = null; }
    try { if (_remoteAudio.node) { _remoteAudio.node.pause(); _remoteAudio.node.srcObject = null; _remoteAudio.node = null; } } catch {}
    setCallState('idle'); callStateRef.current = 'idle';
    setIncomingOffer(null);
    setDisplayName('');
    setIsMuted(false);
    setIsCamOff(false);
  }, [stopRingtone]);

  const createPeer = useCallback((peerId) => {
    const peer = new RTCPeerConnection(ICE_CONFIG);
    peerRef.current      = peer;
    remoteDescSet.current = false;

    peer.onicecandidate = ({ candidate }) => {
      if (candidate && socket) socket.emit('ice-candidate', { to: peerId, candidate });
    };

    let _remoteAccum = null;
    peer.ontrack = (event) => {
      const incoming = event.streams?.[0];
      if (incoming) {
        attachRemote(incoming);
        _remoteAccum = incoming;
        return;
      }
      if (!_remoteAccum) _remoteAccum = new MediaStream();
      _remoteAccum.addTrack(event.track);
      attachRemote(_remoteAccum);
    };

    peer.onconnectionstatechange = () => {
      const state = peer.connectionState;
      if (state === 'failed') {
        import('react-hot-toast').then(m => m.default.error('Call connection failed'));
        cleanup();
      }
      if (state === 'disconnected') {
        const timer = setTimeout(() => {
          if (peer.connectionState === 'disconnected' || peer.connectionState === 'failed') {
            import('react-hot-toast').then(m => m.default.error('Call disconnected'));
            cleanup();
          }
        }, 8000);
        peer._disconnectTimer = timer;
      }
      if (state === 'connected' && peer._disconnectTimer) {
        clearTimeout(peer._disconnectTimer);
        peer._disconnectTimer = null;
      }
    };

    return peer;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, attachRemote, cleanup]);

  const endCall = useCallback(() => {
    const target = activePeerRef.current;
    if (callStateRef.current !== 'idle' && socket && target)
      socket.emit('call-end', { to: target });
    cleanup();
  }, [socket, cleanup]);

  const rejectCall = useCallback(() => {
    const target = incomingOffer?.from || activePeerRef.current;
    if (socket && target) socket.emit('call-reject', { to: target });
    cleanup();
  }, [socket, incomingOffer, cleanup]);

  const toggleMute = useCallback(() => {
    const newMuted = !isMuted;
    localStreamRef.current?.getAudioTracks().forEach(t => { t.enabled = !newMuted; });
    setIsMuted(newMuted);
  }, [isMuted]);

  const toggleCamera = useCallback(() => {
    const newOff = !isCamOff;
    localStreamRef.current?.getVideoTracks().forEach(t => { t.enabled = !newOff; });
    setIsCamOff(newOff);
  }, [isCamOff]);

  // targetId AND isStoreContext now passed PER-CALL (not fixed at hook
  // creation) so the single global instance can correctly handle both
  // regular doctor↔patient calls and medicine-store calls, from anywhere —
  // chat page open or not.
  //
  // `myName` = OUR name, sent to the other side inside the call-offer so
  //            THEY can display who's calling them.
  // `targetName` = the name of the person/store WE are calling — shown on
  //            OUR OWN "Calling..." screen. Defaults to `myName` only if
  //            the caller didn't supply one, to avoid a blank screen.
  const startCall = useCallback(async (type = 'audio', myName = '', targetId, isStoreContext = false, targetName = '') => {
    if (!socket || !targetId || callStateRef.current !== 'idle') return;
    try {
      activePeerRef.current = targetId;
      setCallType(type);
      setDisplayName(targetName || 'Calling...');
      setCallState('calling'); callStateRef.current = 'calling';
      startRingtone();

      ringTimeoutRef.current = setTimeout(() => {
        if (callStateRef.current === 'calling') {
          endCall();
          import('react-hot-toast').then(m => m.default.error('No answer'));
        }
      }, RING_TIMEOUT);

      const stream = await getMedia(type);
      localStreamRef.current = stream;
      safeAttach(localVideoRef.current, stream, true);

      const peer = createPeer(targetId);
      stream.getTracks().forEach(t => {
        peer.addTransceiver(t, { direction: 'sendrecv', streams: [stream] });
      });

      const offer = await peer.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: type === 'video',
      });
      await peer.setLocalDescription(offer);
      socket.emit('call-offer', { to: targetId, offer, callType: type, callerName: myName, isStoreContext });
    } catch (err) {
      endCall();
      import('react-hot-toast').then(m => m.default.error(err.message || 'Call failed'));
    }
  }, [socket, getMedia, createPeer, startRingtone, endCall]);

  const answerCall = useCallback(async () => {
    if (!incomingOffer || !socket || callStateRef.current !== 'ringing') return;
    stopRingtone();
    clearTimeout(ringTimeoutRef.current);
    try {
      activePeerRef.current = incomingOffer.from;
      const stream = await getMedia(callType);
      localStreamRef.current = stream;
      safeAttach(localVideoRef.current, stream, true);

      const peer = createPeer(incomingOffer.from);

      await peer.setRemoteDescription(new RTCSessionDescription(incomingOffer.offer));
      remoteDescSet.current = true;

      const existingTransceivers = peer.getTransceivers();
      stream.getTracks().forEach(track => {
        const freeTransceiver = existingTransceivers.find(
          t => t.receiver?.track?.kind === track.kind && (!t.sender.track)
        );
        if (freeTransceiver) {
          freeTransceiver.direction = 'sendrecv';
          freeTransceiver.sender.replaceTrack(track);
        } else {
          peer.addTransceiver(track, { direction: 'sendrecv', streams: [stream] });
        }
      });

      await flushIce();

      const answer = await peer.createAnswer();
      await peer.setLocalDescription(answer);
      socket.emit('call-answer', { to: incomingOffer.from, answer });

      setCallState('active'); callStateRef.current = 'active';
    } catch (err) {
      rejectCall();
      import('react-hot-toast').then(m => m.default.error(err.message || 'Could not answer'));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingOffer, callType, socket, getMedia, createPeer, stopRingtone, flushIce, rejectCall]);

  useEffect(() => {
    if (callState === 'active' && remoteVideoRef.current && !remoteVideoRef.current.srcObject) {
      attachPendingRemoteStream();
    }
  }, [callState, attachPendingRemoteStream]);

  // Single global listener — accepts calls from ANYONE, regardless of
  // whether any particular chat page is open. This fixes the inconsistent
  // behaviour where calls only worked reliably while a chat page happened
  // to be the one handling them.
  useEffect(() => {
    if (!socket) return;

    const onIncoming = ({ from, offer, callType: ct, callerName: name }) => {
      if (callStateRef.current !== 'idle') {
        socket.emit('call-reject', { to: from });
        return;
      }
      activePeerRef.current = from;
      iceBuf.current        = [];
      remoteDescSet.current = false;
      setIncomingOffer({ from, offer });
      setCallType(ct || 'audio');
      setDisplayName(name || 'Unknown');
      setCallState('ringing'); callStateRef.current = 'ringing';
      startRingtone();
      ringTimeoutRef.current = setTimeout(() => {
        if (callStateRef.current === 'ringing') {
          socket.emit('call-reject', { to: from });
          cleanup();
          import('react-hot-toast').then(m => m.default('Missed call'));
        }
      }, RING_TIMEOUT);
    };

    const onAnswered = async ({ answer }) => {
      if (!peerRef.current || callStateRef.current !== 'calling') return;
      try {
        await peerRef.current.setRemoteDescription(new RTCSessionDescription(answer));
        remoteDescSet.current = true;
        await flushIce();
        setCallState('active'); callStateRef.current = 'active';
      } catch {}
    };

    const onIce = async ({ candidate }) => {
      if (!candidate || callStateRef.current === 'idle') return;
      if (!peerRef.current || !remoteDescSet.current) {
        iceBuf.current.push(candidate); return;
      }
      try { await peerRef.current.addIceCandidate(new RTCIceCandidate(candidate)); } catch {}
    };

    const onEnded = () => {
      if (callStateRef.current !== 'idle') {
        cleanup();
        import('react-hot-toast').then(m => m.default('Call ended'));
      }
    };
    const onRejected = () => {
      if (callStateRef.current !== 'idle') {
        cleanup();
        import('react-hot-toast').then(m => m.default('Call declined', { icon: '📵' }));
      }
    };

    socket.on('incoming-call', onIncoming);
    socket.on('call-answered', onAnswered);
    socket.on('ice-candidate', onIce);
    socket.on('call-ended',    onEnded);
    socket.on('call-rejected', onRejected);

    return () => {
      socket.off('incoming-call', onIncoming);
      socket.off('call-answered', onAnswered);
      socket.off('ice-candidate', onIce);
      socket.off('call-ended',    onEnded);
      socket.off('call-rejected', onRejected);
    };
  }, [socket, startRingtone, cleanup, flushIce]);

  return {
    callState, callType, incomingOffer, callerName: displayName, isMuted, isCamOff,
    activePeerId: activePeerRef.current,
    localVideoRef, remoteVideoRef,
    startCall, answerCall, endCall, rejectCall,
    toggleMute, toggleCamera,
    toggleCam: toggleCamera,
    attachPendingRemoteStream,
  };
}
